import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from 'src/app/modules/core/services/http.service';
import {
  CountByFieldRequestGroup,
  CountGroupByFieldRequest,
  DataMartRequest,
  ResponseMetricsDocumentsResult,
  ResponseMetricsRange,
  ResponseMetricsResult,
  ResponseWorkflowMetricsResult,
  WorkflowStepDetailsType,
  WorkflowStepDocumentPreFilterType,
  WorkflowMetricsDocumentsByTimeRequest,
  GetResponseMetricsDrillDownReportRequest
} from './data.models';
import { SchemaField } from 'src/app/modules/core/store/models/document.model';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { Document } from 'src/app/modules/core/store/models/document.model';

import { API_BASE_URL as apiBaseUrl } from '@app/core/constants/api-base-url';
import { QueryResult } from '@app/modules/core/store/models';

@Injectable({
  providedIn: 'root'
})
export class DataApiService {
  constructor(private http: HttpService) {}

  public getGroupCountData(request: DataMartRequest): Observable<Array<any>> {
    const url = `${apiBaseUrl}/v1/analytics/query/count.groupby`;
    return this.http.post<Array<any>>(url, request);
  }

  public getCountData(request: DataMartRequest): Observable<Array<any>> {
    const url = `${apiBaseUrl}/v1/analytics/query/count.groupby`;
    return this.http.post<Array<any>>(url, request);
  }

  public getStepInTimeData(request: DataMartRequest): Observable<Array<any>> {
    const url = `${apiBaseUrl}/v1/analytics/query/step.time`;
    return this.http.post<Array<any>>(url, request);
  }

  public getDocumentData(request: DataMartRequest, includeCount: boolean): Observable<Array<any>> {
    const queryString = this.http.queryStringFromObject({ includeCount: includeCount });
    
    const url = `${apiBaseUrl}/v1/analytics/query?${queryString}`;
    return this.http.post<Array<any>>(url, request);
  }

  public exportDocumentsToXlsx(request: DataMartRequest): Observable<any> {
    const url = `${apiBaseUrl}/v1/analytics/query/xlsx`;

    return this.http.post(url, request, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  public getAnalyticsFields(type: number, subtype: number): Observable<Array<SchemaField>> {
    const url = `${apiBaseUrl}/v1/analytics/query/${type}/${subtype}/fields`;
    return this.http.get<Array<SchemaField>>(url);
  }

  public syncAll(orgUId: string, profileUId: string, orgCode: string): Observable<any> {
    const url = `${apiBaseUrl}/v1/analytics/sync/${orgUId}`;
    const request = {
      userProfileUId: profileUId,
      orgCode: orgCode
    };
    return this.http.post(url, request);
  }

  public getSyncProgress(orgUId: string, skip: number, take: number, syncUId?: string): Observable<Array<any>> {
    let url = `${apiBaseUrl}/v1/analytics/sync/progress/${orgUId}`;
    if (syncUId) {
      url += `?syncUId=${syncUId}&skip=${skip}&take=${take}`;
    } else {
      url += `?skip=${skip}&take=${take}`;
    }
    return this.http.get<Array<any>>(url);
  }

  public getLatestSyncProgress(orgUId: string): Observable<any> {
    const url = `${apiBaseUrl}/v1/analytics/sync/progress/latest/${orgUId}`;
    return this.http.get<any>(url);
  }

  public getDataset(datasetUId: string): Observable<Dataset> {
    const url = `${apiBaseUrl}/v1/meta/datasets/${datasetUId}?includeWorkflow=true`;

    return this.http.get<Dataset>(url);
  }

  public updateDocument(datasetUId: string, document: Document): Observable<Document[]> {
    const url = `${apiBaseUrl}/v1/analytics/ds/${datasetUId}/documents`;

    const updateRequest = {
      documents: [document]
    };

    return this.http.put<Document[]>(url, updateRequest);
  }

  public getDataCountGroupData(datasetUId: string, request: CountGroupByFieldRequest): Observable<Array<any>> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/query/count.groupby`;
    return this.http.post<Array<any>>(url, request);
  }

  public getWorkflowHistoryData(datasetUId: string, schema: any, queryParams: any): Observable<Array<any>> {
    const url = `${apiBaseUrl}/v1/data/datasets/${datasetUId}/query/workflow.history`;
    const req = {
      schema: schema,
      queryParams: queryParams
    };
    return this.http.post<Array<any>>(url, req);
  }

  public getResponseMetricsData(
    escaledNumberOfMinutes?: number,
    clearedNumberOfMinutes?: number,
    escaledNumberOfSeconds?: number,
    clearedNumberOfSeconds?: number,
    range?: ResponseMetricsRange,
    offset?: number,
    fromDateTimeUnix?: number,
    toDateTimeUnix?: number
  ): Observable<ResponseMetricsResult> {
    escaledNumberOfMinutes = escaledNumberOfMinutes ?? 20;
    clearedNumberOfMinutes = clearedNumberOfMinutes ?? 120;
    escaledNumberOfSeconds = escaledNumberOfSeconds ?? 0;
    clearedNumberOfSeconds = clearedNumberOfSeconds ?? 0;
    range = range || ResponseMetricsRange.Day;
    const req = {
      escaledNumberOfMinutes: escaledNumberOfMinutes,
      clearedNumberOfMinutes: clearedNumberOfMinutes,
      escaledNumberOfSeconds: escaledNumberOfSeconds,
      clearedNumberOfSeconds: clearedNumberOfSeconds,
      offsetFromRange: offset,
      range: range,
      dateTimeFromSeconds: Math.floor(fromDateTimeUnix / 1000),
      dateTimeToSeconds: Math.floor(toDateTimeUnix / 1000)
    };
    const url = `${apiBaseUrl}/v1/analytics/query/response.metrics`;
    return this.http.post<ResponseMetricsResult>(url, req);
  }

  public getResponseWorkflowMetricsData(
    range?: ResponseMetricsRange, 
    offset?: number,
    fromDateTimeUnix?: number,
    toDateTimeUnix?: number
  ): Observable<ResponseWorkflowMetricsResult> {
    range = range || ResponseMetricsRange.Day;
    const req = {
      offsetFromRange: offset,
      range: range,
      dateTimeFromSeconds: Math.floor(fromDateTimeUnix / 1000),
      dateTimeToSeconds: Math.floor(toDateTimeUnix / 1000)
    };
    const url = `${apiBaseUrl}/v1/analytics/query/response.workflow.metrics`;
    return this.http.post<ResponseWorkflowMetricsResult>(url, req);
  }

  public getResponseMetricsDocuments(fromTime: moment.Moment, toTime: moment.Moment): Observable<ResponseMetricsDocumentsResult> {
    const req = {
      fromTime: fromTime.toISOString(),
      toTime: toTime.toISOString()
    };
    const url = `${apiBaseUrl}/v1/data/datasets/query/response.metrics.documents`;
    return this.http.post<ResponseMetricsDocumentsResult>(url, req);
  }

  public getWorkflowResponseMetricsDocuments(request: WorkflowMetricsDocumentsByTimeRequest): Observable<QueryResult<Document>> {
    const url = `${apiBaseUrl}/v1/data/datasets/query/response.workflow.metrics.documents`;
    return this.http.post<QueryResult<Document>>(url, request);
  }

  public createResponseMetricsReport(request: GetResponseMetricsDrillDownReportRequest): Observable<string> {
    const url = `${apiBaseUrl}/v1/data/datasets/report/response.metrics`;
    return this.http.post<string>(url, request);
  }

  public getNotificationFields(): Observable<Array<SchemaField>> {
    const url = `${apiBaseUrl}/v1/analytics/notifications/query/notification/fields`;
    return this.http.get<Array<SchemaField>>(url);
  }

  public getRecipientFields(): Observable<Array<SchemaField>> {
    const url = `${apiBaseUrl}/v1/analytics/notifications/query/recipient/fields`;
    return this.http.get<Array<SchemaField>>(url);
  }

  public getNotificationData(request: DataMartRequest): Observable<Array<any>> {
    const url = `${apiBaseUrl}/v1/analytics/notifications/query/notification.count.groupby`;
    return this.http.post<Array<any>>(url, request);
  }

  public getNotificationRecipientData(request: DataMartRequest): Observable<Array<any>> {
    const url = `${apiBaseUrl}/v1/analytics/notifications/query/recipient.status.count.groupby`;
    return this.http.post<Array<any>>(url, request);
  }

  public getNotificationRecipientResponseData(request: DataMartRequest): Observable<Array<any>> {
    const url = `${apiBaseUrl}/v1/analytics/notifications/query/recipient.response.count.groupby`;
    return this.http.post<Array<any>>(url, request);
  }

  public getNotificationDocuments(request: DataMartRequest): Observable<any> {
    const url = `${apiBaseUrl}/v1/analytics/notifications/query/notification`;
    return this.http.post<Array<any>>(url, request);
  }

  public getRecipientDocuments(request: DataMartRequest): Observable<any> {
    const url = `${apiBaseUrl}/v1/analytics/notifications/query/recipient`;
    return this.http.post<Array<any>>(url, request);
  }

  public exportNotificationDocumentsToXlsx(request: DataMartRequest): Observable<any> {
    const url = `${apiBaseUrl}/v1/analytics/notifications/query/notification/xlsx`;

    return this.http.post(url, request, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  public exportRecipientDocumentsToXlsx(request: DataMartRequest): Observable<any> {
    const url = `${apiBaseUrl}/v1/analytics/notifications/query/recipient/xlsx`;

    return this.http.post(url, request, {
      observe: 'response',
      responseType: 'blob'
    });
  }
}
